import React, { useRef, useEffect, useContext } from 'react';
import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { FETCH_STATE } from 'constants';
import { partnersActions } from 'state/modules/partners';
import { Grid } from '@material-ui/core';
import { RadioButtonsGroup, RadioButton } from 'components/FormFields';
import { UserContext } from 'context';
import * as Yup from 'yup';
import { getValidPartnershipStates } from 'utils';
import { useConfirm } from 'hooks';
import { useStyles } from './Common.styled';
import clsx from 'clsx';

const partnerInviteSchema = () =>
  Yup.object().shape({
    status: Yup.string().required(i18n.t('common.required_field')),
  });

const initialValues = () => ({
  status: '',
});

const UpdatePartnerStatusModal = ({ open, handleCancel, partner }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const updatePartnerState = useSelector((state) => state.partners.updatePartnerState);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const classes = useStyles();

  const { orgType } = useContext(UserContext);
  const validationSchema = partnerInviteSchema();
  const partnerId = partner?.id;

  const {
    allowActivate,
    allowReject,
    allowCancel,
    allowRemove,
    allowRequest,
    allowOnHold,
    disabled,
  } = getValidPartnershipStates(partner?.relationship?.status, orgType, sharingType);

  const loading = updatePartnerState === FETCH_STATE.LOADING;

  const handleSubmit = (values) => {
    dispatch(partnersActions.updatePartnerStatus(partnerId, values.status));
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (updatePartnerState === FETCH_STATE.DONE) {
      handleCancel();
    }
  }, [dispatch, updatePartnerState]);

  const currentStatus = formRef?.current?.values?.status;

  const [toggleUpdateStatus, UpdateStatus] = useConfirm({
    title: i18n.t(
      `partners.statuses_update.${currentStatus}${sharingType === 'FFB' ? '_FFB' : ''}.confirmTitle`
    ),
    message: i18n.t(
      `partners.statuses_update.${currentStatus}${
        sharingType === 'FFB' ? '_FFB' : ''
      }.confirmMessage`
    ),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onSubmit,
    additionalProps: {
      modalProps: { maxWidth: '367px', containerCustomClass: classes.confirmHeader },
      contentProps: {
        className: clsx(
          classes.confirmContent,
          currentStatus === 'CANCELLED' && classes.confirmWarning
        ),
      },
    },
  });

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      //TODO: AGI-1554 Use toggle for FAAS
      onEnter={sharingType === 'FFB' ? toggleUpdateStatus : onSubmit}
      onCancel={handleCancel}
      enableCloseButton={false}
      handleClick={handleCancel}
      enterButtonLabel={i18n.t('common.update')}
      loading={loading}
      title={i18n.t('partners.update_title')}
      maxWidth="532px"
      disabled={disabled}
    >
      <UpdateStatus />
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialValues={initialValues()}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, values, errors, touched, setFieldValue }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <RadioButtonsGroup
                  errors={errors}
                  touched={touched}
                  name="status"
                  value={values.status}
                  setFieldValue={setFieldValue}
                  nonBool
                >
                  {allowRequest ? (
                    <Field
                      value="REQUESTED"
                      component={RadioButton}
                      label={i18n.t('partners.statuses_update.REQUESTED.label')}
                    />
                  ) : null}
                  {allowActivate ? (
                    <>
                      <Field
                        value="ACTIVE"
                        component={RadioButton}
                        className={classes.statusLabel}
                        label={
                          sharingType === 'FAAS' ? (
                            i18n.t('partners.statuses_update.ACTIVE.label')
                          ) : (
                            <>
                              <span
                                className={classes.statusLabelTitle}
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t('partners.statuses_update.ACTIVE_FFB.label'),
                                }}
                              />
                              <span
                                className={classes.statusLabelDescription}
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t(
                                    'partners.statuses_update.ACTIVE_FFB.labelDescription'
                                  ),
                                }}
                              />
                            </>
                          )
                        }
                      />
                    </>
                  ) : null}
                  {allowReject ? (
                    <Field
                      value="REJECTED"
                      component={RadioButton}
                      label={i18n.t('partners.statuses_update.REJECT.label')}
                    />
                  ) : null}
                  {allowOnHold ? (
                    <>
                      <Field
                        value="ONHOLD"
                        component={RadioButton}
                        className={classes.statusLabel}
                        label={
                          <>
                            <span
                              className={classes.statusLabelTitle}
                              dangerouslySetInnerHTML={{
                                __html: i18n.t('partners.statuses_update.ONHOLD_FFB.label'),
                              }}
                            />
                            <span
                              className={classes.statusLabelWarning}
                              dangerouslySetInnerHTML={{
                                __html: i18n.t('partners.statuses_update.ONHOLD_FFB.labelWarning'),
                              }}
                            />
                            <span
                              className={classes.statusLabelDescription}
                              dangerouslySetInnerHTML={{
                                __html: i18n.t(
                                  'partners.statuses_update.ONHOLD_FFB.labelDescription'
                                ),
                              }}
                            />
                          </>
                        }
                      />
                    </>
                  ) : null}
                  {allowCancel ? (
                    sharingType === 'FAAS' ? (
                      <>
                        <Field
                          value="CANCELLED"
                          component={RadioButton}
                          label={i18n.t('partners.statuses_update.CANCELLED.label')}
                        />
                        <Field
                          value="ACTIVE"
                          component={RadioButton}
                          label={i18n.t('partners.statuses_update.CAN_TO_ACTIVE.label')}
                        />
                      </>
                    ) : sharingType === 'FFB' ? (
                      <>
                        <Field
                          value="CANCELLED"
                          component={RadioButton}
                          className={classes.statusLabel}
                          label={
                            <>
                              <span
                                className={classes.statusLabelTitle}
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t('partners.statuses_update.CANCELLED_FFB.label'),
                                }}
                              />
                              <span
                                className={classes.statusLabelWarning}
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t(
                                    'partners.statuses_update.CANCELLED_FFB.labelWarning'
                                  ),
                                }}
                              />
                              <span
                                className={classes.statusLabelDescription}
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t(
                                    'partners.statuses_update.CANCELLED_FFB.labelDescription'
                                  ),
                                }}
                              />
                            </>
                          }
                        />
                      </>
                    ) : null
                  ) : null}
                  {allowRemove ? (
                    <Field
                      value="CANREQUESTED"
                      component={RadioButton}
                      label={i18n.t('partners.statuses_update.CANREQUESTED.label')}
                    />
                  ) : null}
                </RadioButtonsGroup>
                {disabled ? (
                  <p style={{ marginTop: '1rem', color: 'red', fontSize: '0.75rem' }}>
                    {i18n.t('partners.no_update_available')}
                  </p>
                ) : null}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

export { UpdatePartnerStatusModal };
